import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

const Pane = ({ photo, tag }) => (
  <div className="relative aspect-[4/3] overflow-hidden bg-zinc-900">
    <img src={`${BASE}${photo.url}`} alt={`${tag} photo`} loading="lazy"
      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
    <span className={`absolute top-2 left-2 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.25em] ${tag === "BEFORE" ? "bg-black/80 text-zinc-300" : "bg-brand-red text-white"}`}>
      {tag}
    </span>
  </div>
);

export const HighlightsWall = ({ limit, showHeader = true, className = "" }) => {
  const [jobs, setJobs] = useState(null);

  useEffect(() => {
    api.get("/highlights").then((r) => setJobs(r.data)).catch(() => setJobs([]));
  }, []);

  const shown = useMemo(() => {
    const list = limit ? (jobs || []).slice(0, limit) : jobs || [];
    return list.map((j) => ({ ...j, metaLine: [j.vehicle, j.date].filter(Boolean).join(" · ") }));
  }, [jobs, limit]);

  if (!jobs || jobs.length === 0) return null;

  return (
    <section id="our-work" data-testid="highlights-wall" className={className}>
      {showHeader && (
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.4em] text-brand-red">Proof, not promises</span>
            <h2 className="mt-3 font-display uppercase text-4xl sm:text-5xl leading-none tracking-tighter text-white">
              Real work, real results
            </h2>
            <p className="mt-4 max-w-xl text-sm text-zinc-400">
              Before and after shots straight from our Glenorchy workshop floor — actual jobs, actual cars.
            </p>
          </div>
          {limit && jobs.length > limit && (
            <Link to="/about#our-work" data-testid="highlights-see-all"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-wider font-mono text-brand-red hover:text-white transition-colors">
              See more of our work <ArrowRight size={16} />
            </Link>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {shown.map((j) => (
          <div key={j.id} data-testid={`highlight-card-${j.id}`}
            className="group border border-zinc-900 bg-zinc-950 hover:border-brand-red transition-colors">
            {j.before.length > 0 && j.after.length > 0 ? (
              <div className="grid grid-cols-2 gap-px bg-zinc-900">
                <Pane photo={j.before[0]} tag="BEFORE" />
                <Pane photo={j.after[0]} tag="AFTER" />
              </div>
            ) : (
              <Pane photo={(j.after[0] || j.before[0])} tag={j.after.length ? "AFTER" : "BEFORE"} />
            )}
            <div className="p-4">
              <div className="text-white font-semibold text-sm">{j.service}</div>
              {j.caption && <p className="mt-1 text-xs text-zinc-400">{j.caption}</p>}
              <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-zinc-600">
                {j.metaLine}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
